import { useColorMode, useDisclosure } from '@chakra-ui/react'
import {
    call,
    selectAuthenticatedUser,
    selectPresentationNumber,
    useDispatch,
    useSelector
} from '@missionlabs/api'
import { IconButton, Menu, MenuButton, MenuItem, MenuList, useToast } from '@missionlabs/react'
import {
    BlockIcon,
    DeleteIconOutline,
    FlagIconOutline,
    KebabMenuIcon,
    UserCircleIconLight
} from '@missionlabs/react/circleloop'
import { ArrowLeftCorner, EditIconRegular, PhoneIconRegular } from '@missionlabs/react/zeta'
import { Activity, UserActivity, UserRole } from '@missionlabs/types'
import { useActivityFooterState } from 'features/activity/ActivityFeed/hooks/useActivityFooterState'
import { BlockContactDialog } from 'features/contacts/BlockContactDialog'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCanSendWhatsApp } from 'shared/hooks/useCanSendWhatsApp'
import { useFormatToNumberE164 } from 'shared/hooks/useFormatToNumberE164'
import { useGetUserData } from 'shared/hooks/useGetUserData'
import { useIsSMSEnabled } from 'shared/hooks/useIsSMSEnabled'
import { useUserRole } from 'shared/hooks/useUserRole'
import {
    addCallNote,
    CallNoteSliceRootState,
    removeCallNote,
    selectActivityCallNotes
} from 'shared/slices/callNoteSlice'
import {
    useDeleteCallNoteMutation,
    useDeleteFlaggedActivitiesMutation,
    useUpdateFlaggedActivitiesMutation
} from 'shared/store'
import {
    getDirectionOfActivity,
    isAnonymousNumber,
    isInternalActivity
} from 'shared/utils/activities'

import { ActivityFooterMessageType } from '../types'
import { useAddNumberToContactDrawer } from './components/AddNumberToContact/useAddNumberToContactDrawer'

interface ActivityFeedRowMenuProps {
    activity: UserActivity
}

export const ActivityFeedRowMenu = memo(({ activity }: ActivityFeedRowMenuProps) => {
    const { t } = useTranslation()
    const { colorMode } = useColorMode()
    const drawer = useAddNumberToContactDrawer()
    const { formatToNumberE164 } = useFormatToNumberE164()
    const dispatch = useDispatch()
    const { callRoute } = useGetUserData()
    const { toast } = useToast()
    const presentationNumber = useSelector(selectPresentationNumber)
    const role = useUserRole()
    const blockDialog = useDisclosure()

    const callFromNumber = useMemo(
        () => presentationNumber ?? callRoute?.defaultPhoneNumber ?? '',
        [presentationNumber, callRoute?.defaultPhoneNumber]
    )

    const isInbound = useMemo(() => getDirectionOfActivity(activity) === 'inbound', [activity])

    const fromNumber = useMemo(() => {
        const num = isInbound ? activity.dialledNumber : activity.originatingNumber
        return formatToNumberE164(num)
    }, [isInbound, activity, formatToNumberE164])

    const toNumber = useMemo(() => {
        const num = isInbound ? activity.originatingNumber : activity.dialledNumber
        return formatToNumberE164(num)
    }, [isInbound, activity, formatToNumberE164])

    const canBlock = useMemo(() => {
        if (role !== UserRole.ADMIN || !toNumber) return false
        if (isInternalActivity(activity)) return false
        const rawNumber = isInbound ? activity.originatingNumber : activity.dialledNumber
        return !isAnonymousNumber(rawNumber)
    }, [role, toNumber, activity, isInbound])

    const isWhatsAppActivity = useMemo(() => {
        const whatsappActivityTypes = [
            Activity.WHATSAPP_MESSAGE_RECEIVED,
            Activity.WHATSAPP_MESSAGE_SENT
        ]
        return whatsappActivityTypes.includes(activity.activityType)
    }, [activity])

    const user = useSelector(selectAuthenticatedUser)
    const [updateFlaggedActivities] = useUpdateFlaggedActivitiesMutation()
    const [deleteFlaggedActivities] = useDeleteFlaggedActivitiesMutation()

    const [deleteCallNote] = useDeleteCallNoteMutation()

    const { onOpen, setMessageType, setActivity } = useActivityFooterState()

    const isSMSEnabled = useIsSMSEnabled([toNumber], fromNumber)
    const isWhatsAppEnabled = useCanSendWhatsApp()
    const isMessagingEnabled = isWhatsAppActivity ? isWhatsAppEnabled : isSMSEnabled

    const [removedCallNoteIdx, setRemovedCallNoteIdx] = useState<number[]>([])

    useEffect(() => {
        if (!activity) return
        activity.callNotes?.forEach((callNote, index) => {
            // To prevent call notes from being resurrected after they are deleted.
            // Skip call notes whose indexes have been removed.
            // (this prevents us from re-adding old activity notes)
            if (!removedCallNoteIdx.includes(index)) {
                dispatch(
                    addCallNote({
                        callTraceID: activity.callTraceID,
                        note: callNote.content
                    })
                )
            }
        })
    }, [dispatch, activity, removedCallNoteIdx])

    const callNotes = useSelector((state: CallNoteSliceRootState) =>
        selectActivityCallNotes(state, activity?.callTraceID)
    )

    const handleFlag = useCallback(() => {
        if (!user) throw Error('No user')
        if (activity.isFlaggedByUser) throw Error('Activity already flagged by user')
        const payload = {
            userID: user.userID,
            activities: [activity.ID]
        }
        updateFlaggedActivities(payload)
    }, [activity, user, updateFlaggedActivities])

    const handleUnflag = useCallback(() => {
        if (!user) throw Error('No user')
        if (!activity.isFlaggedByUser) throw Error('Activity is not flagged by user')
        const payload = {
            userID: user.userID,
            activities: [activity.ID]
        }
        deleteFlaggedActivities(payload)
    }, [activity, user, deleteFlaggedActivities])

    const handleReturnCall = useCallback(() => {
        if (toNumber && callFromNumber) {
            dispatch(call({ to: toNumber, from: callFromNumber, contact: activity.contact }))
        }
    }, [activity, dispatch, callFromNumber, toNumber])

    const handleReply = useCallback(() => {
        isWhatsAppActivity
            ? setMessageType(ActivityFooterMessageType.WhatsApp)
            : setMessageType(ActivityFooterMessageType.SMS)

        onOpen()
    }, [isWhatsAppActivity, setMessageType, onOpen])

    const handleAddEditNotes = useCallback(() => {
        setMessageType(ActivityFooterMessageType.Notes)
        setActivity(activity)
        onOpen()
    }, [onOpen, setMessageType, setActivity, activity])

    const handleDeleteNotes = useCallback(async () => {
        try {
            // Prefer using slice over activity
            const callNoteID = callNotes?.at(-1)?.ID ?? activity.callNotes?.at(-1)?.ID
            const callTraceID = activity.callTraceID ?? callNotes?.at(-1)?.callTraceID
            // If this is -1, it means activity is empty
            const _removedCallNoteIdx = (activity.callNotes?.length ?? 0) - 1

            if (!callNoteID) throw new Error('No call note ID on userActivity')
            if (!callTraceID) throw new Error('No call trace ID on userActivity')

            await deleteCallNote({ callNoteID }).unwrap()

            if (_removedCallNoteIdx >= 0) {
                // Current activity state contains call notes,
                // mark them as removed so they dont get resurrected
                setRemovedCallNoteIdx([...removedCallNoteIdx, _removedCallNoteIdx])
            }

            dispatch(removeCallNote({ callTraceID }))

            toast({ status: 'success', title: t('callNotes.deleteSuccessful') })
        } catch (err) {
            toast({ status: 'error', title: t('callNotes.deleteError') })
        }
    }, [callNotes, activity, deleteCallNote, dispatch, toast, t, removedCallNoteIdx])

    return (
        <Menu>
            <MenuButton as={IconButton} size="sm" variant="ghost" aria-label={'More'}>
                <KebabMenuIcon boxSize="16px" />
            </MenuButton>
            <MenuList>
                {/* // Todo: Change to appropriate icon */}
                {activity.activityClass === 'call' && (
                    <>
                        <MenuItem icon={<EditIconRegular />} onClick={handleAddEditNotes}>
                            {callNotes && callNotes?.length > 0
                                ? t('activities.notes.editNote')
                                : t('activities.notes.addNote')}
                        </MenuItem>
                        {callNotes && callNotes?.length > 0 && (
                            <MenuItem icon={<DeleteIconOutline />} onClick={handleDeleteNotes}>
                                {t('activities.notes.deleteNote')}
                            </MenuItem>
                        )}
                    </>
                )}

                {activity.isFlaggedByUser ? (
                    <MenuItem icon={<FlagIconOutline />} onClick={handleUnflag}>
                        {t('activities.unflagItem')}
                    </MenuItem>
                ) : (
                    <MenuItem icon={<FlagIconOutline />} onClick={handleFlag}>
                        {t('activities.flagForLater')}
                    </MenuItem>
                )}
                <MenuItem
                    isDisabled={!isMessagingEnabled || !isInbound}
                    icon={
                        <ArrowLeftCorner color={`${colorMode}.primary.gamma`} sx={{ padding: 0 }} />
                    }
                    onClick={handleReply}
                >
                    {t('activities.replyToMessage')}
                </MenuItem>
                {!activity.contactID && (
                    <MenuItem icon={<UserCircleIconLight />} onClick={drawer.onOpen}>
                        {t('contacts.addToExisting')}
                    </MenuItem>
                )}
                {callFromNumber && toNumber && (
                    <MenuItem icon={<PhoneIconRegular />} onClick={handleReturnCall}>
                        {t('activities.returnCall')}
                    </MenuItem>
                )}
                {canBlock && (
                    <MenuItem
                        variant="destructive"
                        icon={<BlockIcon />}
                        onClick={blockDialog.onOpen}
                    >
                        {t('blockedNumbers.blockNumber')}
                    </MenuItem>
                )}
            </MenuList>
            {blockDialog.isOpen && (
                <BlockContactDialog
                    numberE164={toNumber}
                    isOpen={blockDialog.isOpen}
                    onClose={blockDialog.onClose}
                />
            )}
        </Menu>
    )
})
